import { useCallback, useEffect, useState, useRef } from "react";
import { SearchSelect } from "@/components/SearchSelect";
import { useNavigate } from "react-router-dom";
import { api, apiError, eur, dateFmt, num, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { PageHeader, EmptyState, KpiCard, Th, Td, DateInput } from "@/components/common";
import { ListFilter, useListFilter, csvMoney } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MesseBundles from "./MesseBundles";
import MesseTakelist from "@/components/messe/MesseTakelist";
import { toast } from "sonner";
import { Store, Plus, Trash2, TrendingUp, Boxes, FileText, FileSpreadsheet, UploadCloud, Lock, Pencil } from "lucide-react";

const emptyFair = { name: "", location: "", start_date: "", end_date: "", note: "" };

export default function Messe() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  // Messe ist ein Verkaufsthema (Preise, Abrechnung) und bleibt dem Disponenten verschlossen.
  const canEdit = can(user, "messe:edit");
  const [fairs, setFairs] = useState(null);
  const [sel, setSel] = useState(null);
  const [variants, setVariants] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [messeBundles, setMesseBundles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const saveEdit = async () => {
    try {
      await api.put(`/trade-fairs/${sel.id}`, f);
      toast.success(t("me.updated"));
      setEditOpen(false); setF(emptyFair);
      await loadFairs(); loadSel(sel.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteFair = async () => {
    if (!window.confirm(t("me.deleteConfirm", { name: sel.name }))) return;
    try {
      await api.delete(`/trade-fairs/${sel.id}`);
      toast.success(t("me.deleted"));
      setSel(null);
      await loadFairs();
    } catch (e) { toast.error(apiError(e)); }
  };
  const [f, setF] = useState(emptyFair);
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saleEdit, setSaleEdit] = useState(null);
  const fileRef = useRef(null);

  const loadFairs = useCallback(() => api.get("/trade-fairs").then((r) => setFairs(r.data)).catch(() => setFairs([])), []);
  const loadSel = useCallback((id) => api.get(`/trade-fairs/${id}`).then((r) => setSel(r.data)).catch(() => {}), []);
  const loadMesseBundles = useCallback(() => api.get("/messe-bundles").then((r) => setMesseBundles(r.data)).catch(() => {}), []);
  useEffect(() => {
    loadFairs();
    api.get("/variants").then((r) => setVariants([...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de") || (a.sku || "").localeCompare(b.sku || "", "de")))).catch(() => {});
    api.get("/bundles").then((r) => setBundles([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
    loadMesseBundles();
  }, [loadFairs, loadMesseBundles]);

  const createFair = async () => {
    try { const r = await api.post("/trade-fairs", f); toast.success(t("me.created")); setOpen(false); setF(emptyFair); await loadFairs(); loadSel(r.data.id); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addLine = () => setLines([...lines, { _k: crypto.randomUUID(), kind: "variant", ref: "", quantity: 1, unit_price: "" }]);
  const updLine = (i, k, v) => {
    const l = [...lines]; l[i] = { ...l[i], [k]: v };
    if (k === "ref" || k === "kind") {
      if (k === "kind") l[i].ref = "";
      const list = l[i].kind === "bundle" ? bundles : l[i].kind === "messe_bundle" ? messeBundles : variants;
      const src = list.find((x) => x.id === l[i].ref);
      if (src && (src.price_net != null)) l[i].unit_price = src.price_net;
    }
    setLines(l);
  };
  const rmLine = (i) => setLines(lines.filter((_, j) => j !== i));

  const bookSales = async () => {
    setBusy(true);
    try {
      const payload = { lines: lines.filter((l) => l.ref && Number(l.quantity) > 0).map((l) => ({ kind: l.kind, ref: l.ref, quantity: Number(l.quantity), unit_price: Number(l.unit_price) || 0 })) };
      if (!payload.lines.length) { toast.error(t("me.noValidLines")); setBusy(false); return; }
      await api.post(`/trade-fairs/${sel.id}/sales`, payload);
      toast.success(t("me.salesBooked")); setLines([]); loadSel(sel.id); loadFairs();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const importSales = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/trade-fairs/${sel.id}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("me.imported", { count: r.data.created }));
      loadSel(sel.id); loadFairs();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const closeFair = async () => {
    if (!window.confirm(t("me.closeConfirm"))) return;
    try { await api.post(`/trade-fairs/${sel.id}/close`); toast.success(t("me.closed")); loadSel(sel.id); loadFairs(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportXlsx = () => downloadFile(`/trade-fairs/${sel.id}/report.xlsx`, `messe_${(sel.name || "report").replace(/\s/g, "_")}.xlsx`).catch((e) => toast.error(apiError(e)));
  const cancelSale = async (s) => {
    if (!window.confirm(t("me.cancelSaleConfirm", { sku: s.sku, qty: s.quantity }))) return;
    try { await api.delete(`/trade-fairs/${sel.id}/sales/${s.id}`); toast.success(t("me.saleCancelled")); loadSel(sel.id); loadFairs(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveSaleEdit = async () => {
    try {
      await api.put(`/trade-fairs/${sel.id}/sales/${saleEdit.id}`, { quantity: Number(saleEdit.quantity) || 0, unit_price: Number(saleEdit.unit_price) || 0 });
      toast.success(t("me.lineUpdated")); setSaleEdit(null); loadSel(sel.id); loadFairs();
    } catch (e) { toast.error(apiError(e)); }
  };
  const closed = sel?.status === "abgeschlossen";
  const fairFilter = useListFilter(fairs, { fields: ["name", "location", "note"], dateField: "start_date", statusField: "status", storageKey: "fairs" });
  const salesFilter = useListFilter(sel?.sales, { fields: ["sku", "name"], statusField: "status", storageKey: "fairSales" });

  return (
    <div>
      <PageHeader title={t("me.title")} subtitle={t("me.salesHint")}
        actions={canEdit && <Button data-testid="new-fair-btn" onClick={() => { setF(emptyFair); setOpen(true); }}><Plus className="h-4 w-4 mr-1.5" /> {t("me.newFair")}</Button>} />

      <Tabs defaultValue="sales">
        <TabsList data-testid="messe-tabs">
          <TabsTrigger value="sales" data-testid="tab-messe-sales">{t("me.title")}</TabsTrigger>
          <TabsTrigger value="bundles" data-testid="tab-messe-bundles">{t("me.bundlesTab")}</TabsTrigger>
        </TabsList>
        <TabsContent value="sales" className="mt-4">

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-2" data-testid="fairs-list">
          <ListFilter filter={fairFilter} testId="fairs" withDates withStatus placeholder={t("filter.searchFairs")}
            exportName="messen" exportColumns={[
              { label: t("common.name"), value: (fr) => fr.name },
              { label: t("me.location"), value: (fr) => fr.location || "" },
              { label: t("fin.from"), value: (fr) => fr.start_date || "" },
              { label: t("fin.to"), value: (fr) => fr.end_date || "" },
              { label: t("common.status"), value: (fr) => t(`status.${fr.status}`, fr.status || "") },
              { label: t("common.pieces"), value: (fr) => fr.totals?.units || 0 },
              { label: t("common.revenue"), value: (fr) => csvMoney(fr.totals?.revenue) },
              { label: t("common.cogs"), value: (fr) => csvMoney(fr.totals?.cogs) },
              { label: t("common.margin"), value: (fr) => csvMoney(fr.totals?.contribution_margin) },
            ]} />
          {fairs === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
            fairs.length === 0 ? <EmptyState testId="fairs-empty" icon={Store} title="Keine Messen" hint="Legen Sie eine Messe an, um Abverkäufe zu erfassen." /> :
              fairFilter.filtered.map((fr) => (
                <button key={fr.id} onClick={() => loadSel(fr.id)} data-testid={`fair-${fr.id.slice(0, 8)}`}
                  className={`w-full text-left rounded-md border p-3 transition-colors ${sel?.id === fr.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium flex items-center gap-1.5">{fr.name}{fr.status === "abgeschlossen" && <Lock className="h-3 w-3 text-muted-foreground" />}</div>
                    <div className="font-mono text-sm text-primary">{eur(fr.totals?.revenue)}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">{fr.location || "—"} · {fr.start_date || "—"} · {num(fr.totals?.units || 0)} {t("common.pieces")}</div>
                </button>
              ))}
        </div>

        <div className="lg:col-span-2">
          {!sel ? <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">{t("me.selectLeft")}</div> : (
            <div className="space-y-4" data-testid="fair-detail">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-heading text-xl font-semibold flex items-center gap-2">{sel.name}{closed && <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground text-[10px] px-2 py-0.5 font-semibold"><Lock className="h-3 w-3" /> {t("status.abgeschlossen")}</span>}</div>
                  <div className="text-xs text-muted-foreground">{sel.location || "—"} · {sel.start_date || "—"}{sel.end_date ? ` – ${sel.end_date}` : ""}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => nav(`/messe/${sel.id}/report`)} data-testid="fair-report-btn"><FileText className="h-3.5 w-3.5 mr-1" /> {t("me.report")}</Button>
                  <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="fair-xlsx-btn"><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel</Button>
                  {canEdit && !closed && <Button size="sm" variant="outline" onClick={() => { setF({ name: sel.name || "", location: sel.location || "", start_date: sel.start_date || "", end_date: sel.end_date || "", note: sel.note || "" }); setEditOpen(true); }} data-testid="fair-edit-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> {t("common.edit")}</Button>}
                  {canEdit && !closed && <Button size="sm" variant="ghost" onClick={deleteFair} data-testid="fair-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> {t("common.delete")}</Button>}
                  {canEdit && !closed && <Button size="sm" variant="ghost" onClick={closeFair} data-testid="fair-close-btn"><Lock className="h-3.5 w-3.5 mr-1" /> {t("me.close")}</Button>}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="fair-kpi-revenue" index={0} label={t("common.revenue")} value={eur(sel.totals?.revenue)} icon={TrendingUp} tone="primary" />
                <KpiCard testId="fair-kpi-units" index={1} label={t("common.pieces")} value={num(sel.totals?.units || 0)} icon={Boxes} />
                <KpiCard testId="fair-kpi-cogs" index={2} label={t("common.cogs")} value={eur(sel.totals?.cogs)} icon={Boxes} />
                <KpiCard testId="fair-kpi-cm" index={3} label={t("common.margin")} value={eur(sel.totals?.contribution_margin)} icon={TrendingUp} tone="primary" />
              </div>

              {canEdit && !closed && (
                <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-xs text-muted-foreground">
                  {t("me.salesHint")}
                </div>
              )}

              <MesseTakelist fairId={sel.id} fairName={sel.name} canEdit={canEdit} closed={closed}
                variants={variants} bundles={bundles} messeBundles={messeBundles}
                onChanged={() => { loadSel(sel.id); loadFairs(); }} />

              <div className="rounded-md border border-border bg-card p-4 shadow-sm space-y-3" data-testid="messe-sales-section">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1.5"><TrendingUp className="h-4 w-4" /> {t("me.sales")}</Label>
                  {canEdit && !closed && (
                    <div className="flex gap-1.5">
                      <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => importSales(e.target.files?.[0])} data-testid="import-sales-input" />
                      <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-sales-btn"><UploadCloud className="h-3.5 w-3.5 mr-1" /> {t("ow.excelImport")}</Button>
                      <Button size="sm" variant="outline" onClick={addLine} data-testid="sale-add-line"><Plus className="h-3.5 w-3.5 mr-1" /> {t("common.position")}</Button>
                    </div>
                  )}
                </div>

                {canEdit && !closed && lines.length > 0 && (
                  <div className="space-y-2">
                    {lines.map((l, i) => (
                      <div key={l._k || i} className="flex items-center gap-2">
                        <Select value={l.kind} onValueChange={(v) => updLine(i, "kind", v)}><SelectTrigger className="w-32" data-testid={`sale-kind-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="variant">{t("bundle.chooseProduct")}</SelectItem><SelectItem value="bundle">{t("bundle.title")}</SelectItem><SelectItem value="messe_bundle">{t("messebundle.title")}</SelectItem></SelectContent></Select>
                        <SearchSelect className="flex-1" testId={`sale-ref-${i}`} placeholder={t("me.chooseItem")} value={l.ref} onChange={(v) => updLine(i, "ref", v)}
                          options={(l.kind === "bundle" ? bundles : l.kind === "messe_bundle" ? messeBundles : variants).map((x) => ({ value: x.id, label: l.kind === "variant" ? `${x.product_name} · ${x.sku}` : `${x.name} · ${x.sku}` }))} />
                        <Input type="number" className="w-20" placeholder={t("common.quantity")} value={l.quantity} onChange={(e) => updLine(i, "quantity", e.target.value)} data-testid={`sale-qty-${i}`} />
                        <Input type="number" step="0.01" className="w-24" placeholder={t("me.pricePer")} value={l.unit_price} onChange={(e) => updLine(i, "unit_price", e.target.value)} data-testid={`sale-price-${i}`} />
                        <Button size="icon" variant="ghost" onClick={() => rmLine(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    ))}
                    <div className="flex justify-end"><Button onClick={bookSales} disabled={busy} data-testid="book-sales-btn">{t("me.bookSales")}</Button></div>
                  </div>
                )}

                {(sel.sales || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t("me.noSales")}</p>
                ) : (
                  <div className="overflow-x-auto">
                    <ListFilter filter={salesFilter} testId="fair-sales" withStatus placeholder={t("filter.searchProductSku")}
                      exportName={`messe_abverkauf_${(sel.name || "").replace(/\s/g, "_")}`} exportColumns={[
                        { label: "SKU", value: (s) => s.sku },
                        { label: t("op.item"), value: (s) => s.name },
                        { label: t("common.quantity"), value: (s) => s.quantity },
                        { label: t("me.pricePer"), value: (s) => csvMoney(s.unit_price) },
                        { label: t("common.revenue"), value: (s) => csvMoney(s.revenue) },
                        { label: t("ord.colStatus"), value: (s) => t(`status.${s.status}`, s.status || "") },
                      ]} />
                    <table className="w-full"><thead className="bg-muted/50"><tr><Th>SKU</Th><Th>{t("op.item")}</Th><Th right>{t("common.quantity")}</Th><Th right>{t("me.pricePer")}</Th><Th right>{t("common.revenue")}</Th><Th>{t("ord.colStatus")}</Th>{canEdit && !closed && <Th></Th>}</tr></thead>
                      <tbody data-testid="messe-sales-table">
                        {salesFilter.filtered.map((s) => (
                          <tr key={s.id} className={s.status === "storniert" ? "opacity-50" : ""}>
                            <Td mono>{s.sku}</Td><Td>{s.name}</Td>
                            <Td right mono>{s.quantity}</Td>
                            <Td right mono>{eur(s.unit_price)}</Td>
                            <Td right mono>{eur(s.revenue)}</Td>
                            <Td>{s.status === "storniert" ? <span className="text-xs text-destructive">{t("status.storniert")}</span> : <span className="text-xs text-muted-foreground">{t("me.active")}</span>}</Td>
                            {canEdit && !closed && (
                              <Td>{s.status !== "storniert" && (
                                <div className="flex gap-1 justify-end">
                                  <Button size="icon" variant="ghost" onClick={() => setSaleEdit({ ...s })} data-testid={`sale-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                                  <Button size="icon" variant="ghost" onClick={() => cancelSale(s)} data-testid={`sale-cancel-${s.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                </div>
                              )}</Td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
        </TabsContent>
        <TabsContent value="bundles" className="mt-4">
          <MesseBundles onChanged={() => { loadMesseBundles(); if (sel) loadSel(sel.id); }} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!saleEdit} onOpenChange={(o) => !o && setSaleEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("me.editSaleLine")}</DialogTitle></DialogHeader>
          {saleEdit && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">{saleEdit.sku} · {saleEdit.name}</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("common.quantity")}</Label><Input type="number" value={saleEdit.quantity} onChange={(e) => setSaleEdit({ ...saleEdit, quantity: e.target.value })} className="mt-1.5" data-testid="sale-edit-qty" /></div>
                <div><Label>{t("me.pricePer")}</Label><Input type="number" step="0.01" value={saleEdit.unit_price} onChange={(e) => setSaleEdit({ ...saleEdit, unit_price: e.target.value })} className="mt-1.5" data-testid="sale-edit-price" /></div>
              </div>
              <p className="text-xs text-muted-foreground">{t("me.changeQtyHint")}</p>
            </div>
          )}
          <DialogFooter><Button onClick={saveSaleEdit} data-testid="sale-edit-save">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open || editOpen} onOpenChange={(o) => { if (!o) { setOpen(false); setEditOpen(false); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editOpen ? `${t("common.edit")} · ${sel?.name || ""}` : t("me.newFair")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>{t("common.name")} *</Label><Input data-testid="fair-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5"  /></div>
            <div className="col-span-2"><Label>{t("me.location")}</Label><Input data-testid="fair-location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{t("fin.from")}</Label><DateInput value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} className="mt-1.5" testId="fair-start" /></div>
            <div><Label>{t("fin.to")}</Label><DateInput value={f.end_date} onChange={(v) => setF({ ...f, end_date: v })} className="mt-1.5" testId="fair-end" /></div>
            <div className="col-span-2"><Label>{t("common.note")}</Label><Input data-testid="fair-note" value={f.note || ""} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button onClick={editOpen ? saveEdit : createFair} disabled={!f.name?.trim()} data-testid="save-fair-btn">{editOpen ? t("common.save") : t("common.create")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
